// Thin wrapper around vorbis-encoder-js/dist/libvorbis.js (an Emscripten/asm.js
// build of libvorbis, from the ISC-licensed "vorbis-encoder-js" project by
// miyabisun: https://github.com/miyabisun/libvorbis-js).
//
// That build assumes a CommonJS bundler (it unconditionally does
// `module.exports = Module;` at the end). Rather than patch node_modules
// (which npm would clobber on the next install), we predefine a throwaway
// global `module` object before loading it as a plain <script>, then read
// the resulting `Module` global directly — no bundler, no build step.
//
// node_modules isn't served in production, so `scripts/copy-vendor-assets.js`
// (run via the npm preserve/prebuild hooks) copies libvorbis.js into
// public/vendor, where Vue CLI's static-asset handling picks it up for both
// `serve` and `build`.
//
// C API being called (see vorbis-encoder-js/src/encoder.c):
//   encoder_init(numCh, sampleRate, quality) -> encoder_state*
//   encoder_stream_init(enc)                 -> writes Ogg header pages
//   encoder_analysis_buffer(enc, length)     -> float** (per-channel write targets)
//   encoder_process(enc, length)             -> encodes `length` samples (0 = flush)
//   encoder_data_len(enc) / encoder_transfer_data(enc) -> drain pending Ogg bytes
//   encoder_clear(enc)                       -> free encoder state

let modulePromise = null;

function loadModule() {
  if (!modulePromise) {
    modulePromise = new Promise((resolve, reject) => {
      if (!window.module) window.module = {};
      const script = document.createElement('script');
      script.src = `${process.env.BASE_URL}vendor/libvorbis.js`;
      script.onload = () => {
        if (typeof Module === 'undefined' || !Module._encoder_init) {
          reject(new Error('libvorbis.js loaded but did not initialise Module'));
        } else {
          resolve(Module);
        }
      };
      script.onerror = () => reject(new Error(`Failed to load ${script.src}`));
      document.head.appendChild(script);
    });
  }
  return modulePromise;
}

/**
 * Encode mono PCM samples (Float32Array, range [-1, 1]) to an Ogg Vorbis Blob.
 * @param {Float32Array} samples
 * @param {number} sampleRate
 * @param {number} [quality] -0.1 (worst/smallest) .. 1.0 (best/largest)
 */
export async function encodeOggVorbis(samples, sampleRate, quality = 0.4) {
  const Module = await loadModule();
  const numCh = 1;

  const enc = Module._encoder_init(numCh, sampleRate, quality);
  Module._encoder_stream_init(enc);

  const oggChunks = [];
  const drain = () => {
    const len = Module._encoder_data_len(enc);
    if (len > 0) {
      const ptr = Module._encoder_transfer_data(enc);
      oggChunks.push(Module.HEAPU8.slice(ptr, ptr + len));
    }
  };
  drain(); // header pages emitted by encoder_stream_init

  const CHUNK = 4096;
  for (let i = 0; i < samples.length; i += CHUNK) {
    const len = Math.min(CHUNK, samples.length - i);
    // encoder_analysis_buffer returns a byte address to a float*[numCh] array;
    // >>2 converts byte address -> word index (4 bytes/word, matching both
    // HEAPU32 and HEAPF32 element size).
    const ptrArray  = Module._encoder_analysis_buffer(enc, len) >> 2;
    const chanFloats = Module.HEAPU32[ptrArray] >> 2;
    Module.HEAPF32.set(samples.subarray(i, i + len), chanFloats);
    Module._encoder_process(enc, len);
    drain();
  }
  Module._encoder_process(enc, 0); // flush remaining frames
  drain();

  Module._encoder_clear(enc);

  return new Blob(oggChunks, { type: 'audio/ogg' });
}
